/**
 * PDP 颜色色块 / 尺码下拉补全
 *
 * snippets/binding-products.liquid 与 snippets/size-select.liquid 在服务端用
 * paginate 遍历 collection 聚合同款商品，但 Liquid 的 paginate 每页最多只返回 250 个商品，
 * 排在第 250 位之后的同款商品（包括当前商品自身）会丢失。
 *
 * 这里在页面加载后通过 Section Rendering API 请求 sections/pdp-swatch-data.liquid（按 az_style_name 筛选后的完整同款数据），
 * 再按 az_goods_id 精确匹配，用完整结果替换服务端渲染的色块与尺码选项。
 * 请求失败或数据异常时保持服务端渲染结果不变。
 */
(() => {
  // featured-product 等模块可能多次输出 script 标签，只初始化一次
  if (window.__pdpBindingSyncInit) return;
  window.__pdpBindingSyncInit = true;

  // 单个款式商品数量远低于 250，这里限制翻页上限，防止异常数据导致请求过多
  const MAX_PAGES = 5;
  const requestCache = new Map();

  /**
   * 拉取某个款式的全部商品，同一款式只请求一次
   * @param {string} rootUrl 店铺根路径（多语言/市场前缀，如 "/" 或 "/fr"）
   * @param {string} styleName 款式名（az_style_name）
   * @returns {Promise<Array|null>}
   */
  const fetchStyleProducts = (rootUrl, styleName) => {
    const cacheKey = `${rootUrl}|${styleName}`;
    if (requestCache.has(cacheKey)) return requestCache.get(cacheKey);

    const prefix = rootUrl && rootUrl !== '/' ? rootUrl.replace(/\/$/, '') : '';
    const baseUrl = `${prefix}/collections/all?section_id=pdp-swatch-data&filter.p.m.custom.az_style_name=${encodeURIComponent(styleName)}`;
    const fetchPage = async (page) => {
      const response = await fetch(`${baseUrl}&page=${page}`, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      // Section Rendering API 返回 section HTML，数据在其中的 JSON script 标签里
      const html = await response.text();
      const dataNode = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('script[data-pdp-swatch-data]');
      if (!dataNode) throw new Error('swatch data not found');
      return JSON.parse(dataNode.textContent);
    };

    const request = (async () => {
      const firstPage = await fetchPage(1);
      const pages = Math.min(Number(firstPage.pages) || 1, MAX_PAGES);
      const restPages = await Promise.all(
        Array.from({ length: pages - 1 }, (_, index) => fetchPage(index + 2))
      );
      return [firstPage, ...restPages].flatMap((pageData) => pageData.products || []);
    })().catch((error) => {
      // 失败时移除缓存，保持服务端渲染结果
      requestCache.delete(cacheKey);
      console.warn('[pdp-binding-sync] fetch failed:', error);
      return null;
    });

    requestCache.set(cacheKey, request);
    return request;
  };

  /** 与 Liquid sort_natural 保持一致：不区分大小写的字符串排序 */
  const naturalCompare = (a, b) => {
    const keyA = a.toLowerCase();
    const keyB = b.toLowerCase();
    if (keyA < keyB) return -1;
    if (keyA > keyB) return 1;
    return 0;
  };

  /** 从元素 data 属性读取当前商品信息 */
  const readConfig = (node) => ({
    goodsId: (node.dataset.goodsId || '').trim(),
    styleName: (node.dataset.styleName || '').trim(),
    color: (node.dataset.color || '').trim(),
    size: (node.dataset.size || '').trim(),
    handle: node.dataset.handle || '',
    selectedSize: (node.dataset.selectedSize || '').trim(),
    rootUrl: node.dataset.rootUrl || '/',
  });

  /**
   * 计算颜色色块：每个颜色对应一个商品
   * - 当前颜色固定指向当前商品
   * - 其它颜色优先选与当前商品同尺码的商品，避免点击后跳到其它尺码；没有同尺码时沿用旧逻辑取第一个
   */
  const buildColorOptions = (products, config) => {
    const colorMap = new Map();
    products.forEach((item) => {
      if (item.goods_id !== config.goodsId || !item.color) return;
      const picked = colorMap.get(item.color);
      if (item.handle === config.handle) {
        colorMap.set(item.color, item);
      } else if (item.color === config.color) {
        if (!picked) colorMap.set(item.color, item);
      } else if (!picked || (picked.size !== config.size && item.size === config.size)) {
        colorMap.set(item.color, item);
      }
    });
    return Array.from(colorMap.values()).sort((a, b) =>
      naturalCompare(`${a.color}:::${a.handle}`, `${b.color}:::${b.handle}`)
    );
  };

  /** 计算尺码选项：当前颜色下每个尺码取最低价商品（与服务端逻辑一致） */
  const buildSizeOptions = (products, config) => {
    const sizeMap = new Map();
    products.forEach((item) => {
      if (item.goods_id !== config.goodsId || item.color !== config.color || !item.size) return;
      const picked = sizeMap.get(item.size);
      if (!picked || item.price < picked.price) sizeMap.set(item.size, item);
    });
    return Array.from(sizeMap.values()).sort((a, b) =>
      naturalCompare(`${a.size}:::${a.handle}`, `${b.size}:::${b.handle}`)
    );
  };

  /** 用完整数据重建颜色色块，结果与现有 DOM 一致时不做改动 */
  const renderColors = (container, options, config) => {
    const currentLinks = Array.from(container.querySelectorAll('a.swatch-link')).map((link) =>
      link.getAttribute('href')
    );
    const nextLinks = options.map((item) => `/products/${item.handle}`);
    if (currentLinks.join('|') === nextLinks.join('|')) return;

    // 沿用服务端输出的 label for 值，保持结构一致
    const existingLabel = container.querySelector('label.tag-color-swatch');
    const labelFor = existingLabel ? existingLabel.getAttribute('for') : '';
    const fragment = document.createDocumentFragment();

    options.forEach((item) => {
      const isActive = item.handle === config.handle;
      const link = document.createElement('a');
      link.className = 'swatch-link variant-input loadedproduct color-tooltip';
      link.href = `/products/${item.handle}`;
      link.dataset.color = item.color;

      const label = document.createElement('label');
      if (labelFor) label.setAttribute('for', labelFor);
      label.dataset.bgImage = item.image;
      label.className = `variant__button-label ${isActive ? 'active-swatch' : ''} tag-color-swatch`;

      const image = document.createElement('img');
      image.src = item.image;
      image.width = 52;
      image.height = 72;
      image.alt = item.color;
      if (isActive) image.className = 'active-img';

      link.append(label, image);
      fragment.append(link);
    });

    container.replaceChildren(fragment);
  };

  /** 用完整数据重建尺码下拉，结果与现有 DOM 一致时不做改动 */
  const renderSizes = (select, options, config) => {
    const currentKeys = Array.from(select.options).map((option) => `${option.value}>${option.dataset.url}`);
    const nextKeys = options.map((item) => `${item.size}>/products/${item.handle}`);
    if (currentKeys.join('|') === nextKeys.join('|')) return;

    const fragment = document.createDocumentFragment();
    options.forEach((item) => {
      const option = document.createElement('option');
      option.value = item.size;
      option.textContent = item.size;
      option.dataset.url = `/products/${item.handle}`;
      if (item.size === config.selectedSize) {
        option.selected = true;
        option.setAttribute('selected', 'selected');
      }
      fragment.append(option);
    });

    select.replaceChildren(fragment);
    if (config.selectedSize) select.value = config.selectedSize;
  };

  const syncNode = async (node) => {
    const config = readConfig(node);
    if (!config.goodsId || !config.styleName || !config.color) return;

    const products = await fetchStyleProducts(config.rootUrl, config.styleName);
    if (!products || products.length === 0) return;

    if (node.dataset.bindingSync === 'colors') {
      const options = buildColorOptions(products, config);
      // 数据里找不到当前商品时视为异常，保持服务端结果
      if (options.some((item) => item.handle === config.handle)) renderColors(node, options, config);
    } else if (node.dataset.bindingSync === 'sizes') {
      const options = buildSizeOptions(products, config);
      if (options.some((item) => item.size === config.selectedSize)) renderSizes(node, options, config);
    }
  };

  const init = () => {
    document.querySelectorAll('[data-binding-sync]').forEach((node) => {
      syncNode(node);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
